/**
 * The on-chart half of area downloads (fb-msn0j0jv-3): the hint while a box is
 * being drawn, and the confirm card once an area has resolved to packs.
 *
 * The confirm card lives on the chart rather than back in the menu, because
 * the decision it asks for ("this water, these megabytes?") is about what is
 * on the screen, and a card two menus away from the box it describes would be
 * answered from memory.
 */
import { useMemo } from 'react';
import { useChartPacksStore } from '@/store/chartPacks';
import { formatPackBytes } from '@/chart-sources/packSelection';
import { useShellStore } from '@/store/shell';
import { haptic } from '@/ui/haptics';

/** Shown while the next press-drag will draw the download box. */
export function AreaSelectHint() {
  const armed = useChartPacksStore((s) => s.areaSelectArmed);
  const cancel = useChartPacksStore((s) => s.cancelAreaSelect);
  if (!armed) return null;
  return (
    <div className="acp-hint">
      Drag a box over the water you want to carry offline.
      <button
        type="button"
        className="acp-hint-cancel"
        onClick={cancel}
        aria-label="Cancel choosing an area"
      >
        Cancel
      </button>
    </div>
  );
}

/** The staged selection: what the area came to, and the one deliberate button. */
export function AreaDownloadCard() {
  const stagedPackIds = useChartPacksStore((s) => s.stagedPackIds);
  const packs = useChartPacksStore((s) => s.packs);
  const downloadStaged = useChartPacksStore((s) => s.downloadStaged);
  const clearStaged = useChartPacksStore((s) => s.clearStaged);
  const showToast = useShellStore((s) => s.showToast);

  const staged = useMemo(
    () => (stagedPackIds ? packs.filter((p) => stagedPackIds.includes(p.id)) : []),
    [stagedPackIds, packs],
  );
  if (stagedPackIds === null) return null;

  if (staged.length === 0) {
    return (
      <div className="acp-hint">
        That area is already aboard, or no charts cover it.
        <button type="button" className="acp-hint-cancel" onClick={clearStaged} aria-label="Dismiss">
          OK
        </button>
      </div>
    );
  }

  const bytes = staged.reduce((t, p) => t + p.sizeBytes, 0);
  const names = staged.map((p) => p.region).join(', ');
  return (
    <div className="acp-hint" role="dialog" aria-label="Confirm chart download">
      <span>
        {staged.length} {staged.length === 1 ? 'area' : 'areas'} · {formatPackBytes(bytes)} — {names}
      </span>
      <button
        type="button"
        className="acp-hint-cancel"
        onClick={() => {
          haptic('commit');
          void downloadStaged();
          showToast(`Downloading ${staged.length} ${staged.length === 1 ? 'area' : 'areas'} — progress is in Chart provider`);
        }}
        aria-label={`Download ${formatPackBytes(bytes)} of charts`}
      >
        Download
      </button>
      <button type="button" className="acp-hint-cancel" onClick={clearStaged} aria-label="Cancel the download">
        Cancel
      </button>
    </div>
  );
}
